/**
 * Profile obligation deep links for notifications and obligations grid.
 */

import { resolveResponsibleOrganizationId } from '@/shared/auth/teamLeadCapabilities';

/** Poruka kada moderator nema pristup detalju obaveze u statusu „Предложена“ (CREATED). */
export const OBLIGATION_CREATED_MODERATOR_ACCESS_MESSAGE =
  'Преглед обавезе није доступан док је у статусу „Предложена“. Обавеза ће бити доступна када пређе у „Узета у разматрање“.';

export const OBLIGATION_NOTIFICATION_TYPES = new Set([
  'MODERATOR_ASSIGNED_TO_OBLIGATION',
  'MODERATOR_REJECTED_OBLIGATION',
  'OBLIGATION_CREATED_PENDING_REVIEW',
  'OBLIGATION_SUBMITTED_FOR_REVIEW',
  'OBLIGATION_STATUS_CHANGED',
  'OBLIGATION_ACTIVITY_STATUS_CHANGED',
  'OBLIGATION_PROPOSAL_FINALIZED',
  'OBLIGATION_PROPOSAL_UPDATED',
  'IMPLEMENTATION_DELAY_REPORTED',
]);

/**
 * Moderator na profilu ne otvara detalj obaveze dok je u PROPOSAL fazi (CREATED / DRAFT / nepoznat status).
 * @param {string|null|undefined} obligationStatus
 * @returns {boolean}
 */
export function isObligationModeratorProfileDetailBlocked(obligationStatus) {
  const status = String(obligationStatus ?? '').trim().toUpperCase();
  if (!status) return true;
  return status === 'CREATED' || status === 'DRAFT';
}

/**
 * @param {object|null|undefined} obl
 * @returns {string|null}
 */
export function resolveResponsibleInstitutionIdFromObligation(obl) {
  if (!obl) return null;
  const list = obl.responsible_institutions;
  if (Array.isArray(list) && list.length > 0) {
    const main =
      list.find((entry) => entry?.main_responsible === true || entry?.mainResponsible === true)
      || list[0];
    if (main == null) return null;
    if (typeof main === 'string' || typeof main === 'number') {
      const s = String(main).trim();
      return s || null;
    }
    const id = main.institution_id ?? main.organization_id ?? main.id ?? null;
    return id != null && String(id).trim() !== '' ? String(id).trim() : null;
  }
  const related = obl.related_organizations ?? obl.relatedOrganizations;
  if (Array.isArray(related) && related.length > 0) {
    const resolved = resolveResponsibleOrganizationId(related);
    return resolved || null;
  }
  return null;
}

/**
 * @param {{ actionPlanId?: string|number|null, institutionId?: string|number|null }} [options]
 * @returns {string}
 */
export function buildProfileObligationsTabHref({ actionPlanId, institutionId } = {}) {
  const params = new URLSearchParams({ tab: 'obaveze' });
  if (actionPlanId != null && actionPlanId !== '') {
    params.set('actionPlanId', String(actionPlanId));
  }
  if (institutionId != null && institutionId !== '') {
    params.set('institutionId', String(institutionId));
  }
  return `/profil?${params.toString()}`;
}

/**
 * @param {string|number} obligationId
 * @param {string|number} apId
 * @param {string|null|undefined} apStatus
 * @returns {string|null}
 */
export function buildProfileObligationHref(obligationId, apId, apStatus, obligationStatus = null) {
  if (!obligationId || !apId) return null;
  const params = new URLSearchParams({ tab: 'obaveze', actionPlanId: String(apId) });
  if (apStatus === 'IMPLEMENTATION') {
    return `/profil/pracenje-sprovodjenja/${obligationId}?${params.toString()}`;
  }
  if (String(obligationStatus || '').toUpperCase() === 'DRAFT') {
    return `/profil/predlog-obaveze/${obligationId}?${params.toString()}`;
  }
  return `/profil/predlog-obaveze/${obligationId}?${params.toString()}`;
}

/**
 * Link za obavezu u profilu — uvek koristi entityId; actionPlanId dodaje kad je poznat.
 * @param {string|null|undefined} entityId
 * @param {object|null|undefined} obl
 * @returns {{ key: string, to: string, label: string, linkHint?: string }}
 */
export function buildProfileObligationNotificationLink(entityId, obl) {
  if (!entityId) {
    return { key: 'obaveze', to: '/profil?tab=obaveze', label: 'Обавезе' };
  }
  const apId = obl?.action_plan_id ?? obl?._apId ?? obl?.actionPlanId ?? null;
  const apStatus = obl?._apStatus ?? obl?.action_plan_status ?? null;
  const href =
    buildProfileObligationHref(entityId, apId, apStatus, obl?.status) ??
    (() => {
      const params = new URLSearchParams({ tab: 'obaveze' });
      if (apId) params.set('actionPlanId', String(apId));
      return `/profil/predlog-obaveze/${entityId}?${params.toString()}`;
    })();
  return {
    key: 'obl-profile',
    to: href,
    label: 'Преглед',
    linkHint: apStatus === 'IMPLEMENTATION' ? 'Праћење спровођења' : 'Преглед обавезе',
  };
}

/**
 * Link za moderator obaveštenja — CREATED vodi na listu obaveza institucije, ne na detalj.
 * @param {string|null|undefined} entityId
 * @param {object|null|undefined} obl
 * @returns {{ key: string, to: string, label: string, linkHint?: string }}
 */
export function buildModeratorObligationNotificationLink(entityId, obl) {
  const apId = obl?.action_plan_id ?? obl?._apId ?? obl?.actionPlanId ?? null;
  const status = obl?.status ?? null;
  const institutionId = resolveResponsibleInstitutionIdFromObligation(obl);

  if (!entityId || isObligationModeratorProfileDetailBlocked(status)) {
    return {
      key: 'obaveze',
      to: buildProfileObligationsTabHref({ actionPlanId: apId, institutionId }),
      label: 'Обавезе',
      linkHint: 'Листа обавеза',
    };
  }

  return buildProfileObligationNotificationLink(entityId, obl);
}

/**
 * Kontekstualni tekst za MODERATOR_ASSIGNED obaveštenje.
 * @param {string} obligationName
 * @param {string|null|undefined} obligationStatus
 * @returns {string}
 */
export function buildModeratorAssignedNotificationContext(obligationName, obligationStatus) {
  const name = obligationName?.trim() || 'Обавеза у припреми';
  if (isObligationModeratorProfileDetailBlocked(obligationStatus)) {
    return `${name}: додељена вам је улога модератора. ${OBLIGATION_CREATED_MODERATOR_ACCESS_MESSAGE}`;
  }
  return `${name}: прихватите или одбијте улогу модератора на обавези у припреми акционог плана.`;
}

/**
 * @param {object[]} notifications
 * @returns {string[]}
 */
export function collectObligationIdsFromNotifications(notifications = []) {
  return [
    ...new Set(
      (notifications || [])
        .filter((n) => {
          const type = n?.notification_type || n?.type || '';
          return OBLIGATION_NOTIFICATION_TYPES.has(type) && type !== 'OBLIGATION_PROPOSAL_UPDATED';
        })
        .map((n) => n?.related_entity_id ?? n?.relatedEntityId)
        .filter((id) => id != null && id !== '')
        .map(String),
    ),
  ];
}

/**
 * @param {string|number} obligationId
 * @param {object[]} obligations
 * @param {object[]} obligationsInPreparation
 * @returns {object|null}
 */
export function findObligationInLists(obligationId, obligations = [], obligationsInPreparation = []) {
  if (!obligationId) return null;
  const idStr = String(obligationId);
  const fromPrep = (obligationsInPreparation || []).find((o) => String(o?.id) === idStr);
  if (fromPrep) return fromPrep;
  return (obligations || []).find((o) => String(o?.id) === idStr) || null;
}

/**
 * @param {object|null} obligation
 * @returns {string|null}
 */
export function buildProfileObligationHrefFromObligation(obligation) {
  if (!obligation?.id) return null;
  const apId = obligation.action_plan_id ?? obligation._apId ?? obligation.actionPlanId;
  const apStatus = obligation._apStatus ?? obligation.action_plan_status ?? null;
  return buildProfileObligationHref(obligation.id, apId, apStatus);
}
